import { useQueryClient } from 'react-query';
import { useSchemaQueryUpdaters } from '@frontend/react-query-helpers';
import { serviceName } from '../service';
import { BlockedNumber, ServiceQueries } from '../types';

/**
 * This hook is intended to be used by react-query mutation hooks internally in api libraries.
 * Other use cases should use the corresponding mutations instead.
 */
export const useCallBlockV1QueryUpdaters = () => {
  const queryClient = useQueryClient();
  const queryUpdaters = useSchemaQueryUpdaters<ServiceQueries>(serviceName);
  const { getQueryKey, getQueriesData, updateQuery, invalidateQueries } = queryUpdaters;

  const getTenantQueryFilters = (tenantId: string) => ({
    queryKey: getQueryKey<'ListBlockedNumbers'>({ endpointName: 'ListBlockedNumbers', request: { tenantId } }),
  });

  /** Stops in-flight list fetches for the tenant so they can't overwrite an optimistic update. */
  const cancelBlockedNumbersQueries = (tenantId: string) =>
    queryClient.cancelQueries(getTenantQueryFilters(tenantId).queryKey);

  const findBlockedNumber = (tenantId: string, id: string): BlockedNumber | undefined =>
    getQueriesData({ endpointName: 'ListBlockedNumbers', queryFilters: getTenantQueryFilters(tenantId) })
      .flatMap(([, data]) => data.blockedNumbers ?? [])
      .find((blockedNumber) => blockedNumber.id === id);

  const addBlockedNumber = (tenantId: string, blockedNumber: BlockedNumber) =>
    updateQuery({
      endpointName: 'ListBlockedNumbers',
      queryFilters: getTenantQueryFilters(tenantId),
      updater: (old) => ({ ...old, blockedNumbers: [...(old.blockedNumbers ?? []), blockedNumber] }),
    });

  const removeBlockedNumber = (tenantId: string, id: string) =>
    updateQuery({
      endpointName: 'ListBlockedNumbers',
      queryFilters: getTenantQueryFilters(tenantId),
      updater: (old) => ({ ...old, blockedNumbers: old.blockedNumbers?.filter((item) => item.id !== id) }),
    });

  const replaceBlockedNumber = (tenantId: string, id: string, blockedNumber: BlockedNumber) =>
    updateQuery({
      endpointName: 'ListBlockedNumbers',
      queryFilters: getTenantQueryFilters(tenantId),
      updater: (old) => ({
        ...old,
        blockedNumbers: old.blockedNumbers?.map((item) => (item.id === id ? blockedNumber : item)),
      }),
    });

  const invalidateBlockedNumbers = (tenantId: string) =>
    invalidateQueries({ endpointName: 'ListBlockedNumbers', queryFilters: getTenantQueryFilters(tenantId) });

  return {
    ...queryUpdaters,
    cancelBlockedNumbersQueries,
    findBlockedNumber,
    addBlockedNumber,
    removeBlockedNumber,
    replaceBlockedNumber,
    invalidateBlockedNumbers,
  };
};
